import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, Filter, X, Image as ImageIcon, ChevronLeft, ChevronRight, Grid2x2, LayoutGrid } from 'lucide-react';
import { toast } from 'react-toastify';
import { getProducts } from '../api/products';
import { getBrands, getSizes, getTileTypes } from '../api/categories';
import ProductCatalogueCard from '../components/catalogue/ProductCatalogueCard';
import ProductCatalogueModal from '../components/catalogue/ProductCatalogueModal';

const ProductCataloguePage = () => {
  // Products state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Categories for filters
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [tileTypes, setTileTypes] = useState([]);

  // View mode state - 'compact' or 'large'
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('catalogueViewMode') || 'compact';
  });

  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('catalogueViewMode', viewMode);
  }, [viewMode]);

  // Filter state
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
    brandId: '',
    sizeId: '',
    typeId: '',
    productType: 'tile', // Only show tiles in catalogue
    isActive: true,
    sortBy: 'name',
    sortOrder: 'ASC'
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasMore: false
  });

  const [showFilters, setShowFilters] = useState(false);

  // Modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [brandsData, sizesData, tileTypesData] = await Promise.all([
          getBrands(),
          getSizes(),
          getTileTypes()
        ]);
        setBrands(brandsData);
        setSizes(sizesData);
        setTileTypes(tileTypesData);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getProducts(filters);
      setProducts(response.products || []);
      setPagination(response.pagination || {
        page: filters.page,
        limit: filters.limit,
        total: 0,
        totalPages: 0,
        hasMore: false
      });
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      search: '',
      brandId: '',
      sizeId: '',
      typeId: '',
      productType: 'tile',
      isActive: true,
      sortBy: 'name',
      sortOrder: 'ASC'
    });
  };

  // Check if any filters are active
  const hasActiveFilters = filters.brandId || filters.sizeId || filters.typeId || filters.search;

  // Handle product card click
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  // Pagination handlers
  const goToPage = (page) => {
    setFilters(prev => ({ ...prev, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get grid classes based on view mode
  const getGridClasses = () => {
    if (viewMode === 'compact') {
      // Compact: 2 cols mobile, 3 cols tablet, 4 cols medium, 5 cols desktop
      return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4';
    }
    // Large: 1 col mobile, 2 cols small, 3 cols tablet, 4 cols desktop
    return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6';
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-1 sm:gap-2 mt-8">
        <button
          onClick={() => goToPage(pagination.page - 1)}
          disabled={pagination.page === 1}
          className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => goToPage(1)}
              className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              1
            </button>
            {startPage > 2 && <span className="px-2 text-gray-400">...</span>}
          </>
        )}

        {pages.map(page => (
          <button
            key={page}
            onClick={() => goToPage(page)}
            className={`px-3 py-2 rounded-lg border transition-colors ${
              page === pagination.page
                ? 'bg-accent-gold text-primary-black border-accent-gold font-semibold'
                : 'border-gray-300 hover:bg-gray-100'
            }`}
          >
            {page}
          </button>
        ))}

        {endPage < pagination.totalPages && (
          <>
            {endPage < pagination.totalPages - 1 && <span className="px-2 text-gray-400">...</span>}
            <button
              onClick={() => goToPage(pagination.totalPages)}
              className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              {pagination.totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => goToPage(pagination.page + 1)}
          disabled={pagination.page === pagination.totalPages}
          className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary-black flex items-center gap-3">
                <ImageIcon className="w-8 h-8 text-accent-gold" />
                Product Catalogue
              </h1>
              <p className="text-sm md:text-base text-gray-600 mt-1">
                Browse products by images • Click to view gallery
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">
                Showing <span className="font-semibold">{products.length}</span> of <span className="font-semibold">{pagination.total}</span> products
              </p>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex flex-col md:flex-row gap-3 md:gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                />
              </div>

              {/* View Mode Toggle & Filter Buttons */}
              <div className="flex gap-2">
                {/* View Mode Switcher */}
                <div className="flex items-center gap-0.5 px-1.5 py-1 border border-gray-300 rounded-lg bg-gray-50">
                  <button
                    onClick={() => setViewMode('compact')}
                    className={`p-2 rounded transition-colors ${
                      viewMode === 'compact'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Compact View"
                  >
                    <Grid2x2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('large')}
                    className={`p-2 rounded transition-colors ${
                      viewMode === 'large'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Large View"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors ${
                    hasActiveFilters
                      ? 'bg-accent-gold border-accent-gold text-primary-black font-semibold'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Filter className="w-5 h-5" />
                  <span className="hidden sm:inline">Filters</span>
                  {hasActiveFilters && (
                    <span className="bg-primary-black text-white text-xs px-1.5 py-0.5 rounded-full">
                      {[filters.brandId, filters.sizeId, filters.typeId].filter(Boolean).length}
                    </span>
                  )}
                </button>
                <button
                  onClick={fetchProducts}
                  className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Filter Dropdowns */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Brand Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                    <select
                      value={filters.brandId}
                      onChange={(e) => handleFilterChange('brandId', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="">All Brands</option>
                      {brands.map(brand => (
                        <option key={brand.id} value={brand.id}>
                          {brand.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Size Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                    <select
                      value={filters.sizeId}
                      onChange={(e) => handleFilterChange('sizeId', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="">All Sizes</option>
                      {sizes.map(size => (
                        <option key={size.id} value={size.id}>
                          {size.dimension}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Tile Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tile Type</label>
                    <select
                      value={filters.typeId}
                      onChange={(e) => handleFilterChange('typeId', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="">All Types</option>
                      {tileTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold mx-auto mb-4"></div>
              <p className="text-gray-600">Loading catalogue...</p>
            </div>
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl shadow-md">
            <ImageIcon className="w-20 h-20 text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No products found</h3>
            <p className="text-gray-500 mb-4">Try adjusting your filters or search terms</p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
              >
                <X className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className={`grid ${getGridClasses()}`}>
              {products.map(product => (
                <ProductCatalogueCard
                  key={product.id}
                  product={product}
                  viewMode={viewMode}
                  onClick={() => handleProductClick(product)}
                />
              ))}
            </div>

            {/* Pagination */}
            {renderPagination()}
          </>
        )}
      </div>

      {/* Product Modal */}
      <ProductCatalogueModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        product={selectedProduct}
      />
    </div>
  );
};

export default ProductCataloguePage;
